import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SchoolRegistration = () => {
  const { user, checkAuth, setUser } = useContext(AuthContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [branding, setBranding] = useState({ logo_url: null, site_name: 'Train In Japan' });
  const [formData, setFormData] = useState({
    user_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    school_name: '',
    description: '',
    location: '',
    contact_phone: '',
    website: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/branding/logo`).then(res => setBranding(res.data)).catch(err => console.error('Branding error:', err));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    // Validate password length
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);
    try {
      // Use the combined school-signup endpoint
      const response = await axios.post(`${API}/auth/school-signup`, {
        user_name: formData.user_name,
        email: formData.email,
        password: formData.password,
        school_name: formData.school_name,
        description: formData.description,
        location: formData.location,
        contact_phone: formData.contact_phone,
        website: formData.website
      }, { withCredentials: true });
      
      // Set user from response
      const userData = response.data.user;
      setUser(userData);
      
      toast.success('School account created successfully! Redirecting to your dashboard...');
      
      // Navigate to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error) {
      console.error('School registration failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to register school';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = () => {
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    const redirectUrl = `${window.location.origin}/register-school`;
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Floating Logo */}
      <div className="fixed top-2 sm:top-3 left-3 sm:left-4 md:left-6 z-[60] cursor-pointer" onClick={() => navigate('/')}>
        <div className="flex flex-col items-center" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}>
          {branding.logo_url && (
            <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-white border-2 sm:border-[3px] border-emerald-600 overflow-hidden">
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <span className="mt-1 text-xs sm:text-sm md:text-base font-bold text-slate-800 bg-white/90 px-2 py-0.5 rounded-full" style={{ fontFamily: 'Playfair Display, serif' }}>{branding.site_name}</span>
        </div>
      </div>

      {/* Slim Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="w-8 sm:w-12"></div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-slate-700 hover:text-emerald-800 font-medium transition-colors">Home</button>
            <button onClick={() => navigate('/programs')} className="text-slate-700 hover:text-emerald-800 font-medium transition-colors">Programs</button>
            {user ? (
              <Button onClick={() => navigate('/dashboard')} variant="default" className="bg-emerald-700 hover:bg-emerald-800">Dashboard</Button>
            ) : (
              <Button data-testid="login-btn" onClick={handleLogin} variant="default" className="bg-emerald-700 hover:bg-emerald-800">Sign In</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Registration Form */}
      <section data-testid="school-registration-section" className="py-12 px-4 sm:px-6">
        <div className="max-w-full sm:max-w-3xl mx-auto px-2 sm:px-0">
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Register Your School</h1>
            <p className="text-lg text-slate-600">Join our platform and share your authentic Japanese martial arts or cultural programs with students worldwide.</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Spectral, serif' }}>School Information</CardTitle>
              <CardDescription>
                Fill in your school details and get immediate access. You can add locations, instructors, and courses right away. 
                <strong className="text-emerald-700"> Note: Your first course will need admin approval before it's published.</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Account Information Section */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-emerald-900 mb-4">Account Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Your Full Name *</label>
                      <input 
                        data-testid="user-name-input"
                        type="text" 
                        required
                        value={formData.user_name}
                        onChange={(e) => setFormData({...formData, user_name: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.fullNamePlaceholder")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                      <input 
                        data-testid="user-email-input"
                        type="email" 
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.emailPlaceholder")}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Password *</label>
                        <input 
                          data-testid="user-password-input"
                          type="password" 
                          required
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                          placeholder={t("schoolRegistration.passwordPlaceholder")}
                          minLength={6}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password *</label>
                        <input 
                          data-testid="user-confirm-password-input"
                          type="password" 
                          required
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                          placeholder={t("schoolRegistration.confirmPasswordPlaceholder")}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* School Information Section */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">School Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">School Name *</label>
                      <input 
                        data-testid="school-name-input"
                        type="text" 
                        required
                        value={formData.school_name}
                        onChange={(e) => setFormData({...formData, school_name: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.schoolNamePlaceholder")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                      <textarea 
                        data-testid="school-description-input"
                        required
                        rows={4}
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.descriptionPlaceholder")}
                      ></textarea>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Location (City, Prefecture) *</label>
                      <input 
                        data-testid="school-location-input"
                        type="text" 
                        required
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.locationPlaceholder")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Contact Phone</label>
                      <input 
                        data-testid="school-phone-input"
                        type="tel" 
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.phonePlaceholder")}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Website</label>
                      <input 
                        data-testid="school-website-input"
                        type="url" 
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                        placeholder={t("schoolRegistration.websitePlaceholder")}
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <p className="text-sm text-emerald-950">
                    <strong>✓ Instant Access:</strong> Your account and school will be created immediately. You can start adding locations, instructors, and programs right away. Your first course will need admin approval before going live.
                  </p>
                </div>

                <Button 
                  data-testid="school-submit-btn"
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 py-6 text-lg"
                >
                  {submitting ? 'Submitting...' : 'Submit Registration'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default SchoolRegistration;
